import { getAxisType, getPoint } from './get-datapoints.js';
import { numberOfCoordinatesByDimension } from './number-of-coordinates-by-dimension.js';
import type {
  XYChartAxisInternal,
  XYChartBarSeriesInternal,
} from '../../../types/xy-chart-internals.js';
import type { XYChartData } from '../../../types/xy-chart.js';

/**
 * Generates a bar from the provided datum and series properties.
 *
 * @param datum - The individual data point.
 * @param seriesProps - The series path properties for the line.
 * @param xAxis - The x-axis internal configuration.
 * @param yAxis - The y-axis internal configuration.
 * @returns - The bar series data point, or undefined if any required value is missing or invalid.
 */
export function getBarDatapoint(
  datum: XYChartData,
  seriesProps: XYChartBarSeriesInternal,
  xAxis: XYChartAxisInternal,
  yAxis: XYChartAxisInternal,
) {
  const xAxisType = getAxisType('x', xAxis, yAxis);
  const yAxisType = getAxisType('y', xAxis, yAxis);

  const point = {
    x0: getPoint(datum, seriesProps.x0Accessor, xAxisType),
    x1: getPoint(datum, seriesProps.x1Accessor, xAxisType),
    y0: getPoint(datum, seriesProps.y0Accessor, yAxisType),
    y1: getPoint(datum, seriesProps.y1Accessor, yAxisType),
  };

  const { xCoordinateCount, yCoordinateCount } =
    numberOfCoordinatesByDimension(point);

  // Early return when missing whole axis or having just one coordinate (which doesn't produce an bar)
  if (
    xCoordinateCount === 0 ||
    yCoordinateCount === 0 ||
    xCoordinateCount + yCoordinateCount === 1
  ) {
    return undefined;
  }

  // Prevent bars that have both coords on the same axis defined
  if (xCoordinateCount + yCoordinateCount === 2) {
    const invalidCombinations =
      (point.x0 !== undefined && point.y0 !== undefined) ||
      (point.x1 !== undefined && point.y1 !== undefined);

    if (invalidCombinations) {
      return undefined;
    }
  }

  return {
    ...datum,
    ...point,
  };
}
